import React, { useEffect, useRef } from "react";

/** End of the list: loads the next page when scrolled near, and says how much is shown. */
const ProductListMore = ({
  shown,
  total,
  hasMore,
  loading,
  failed,
  onMore,
}: {
  shown: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  failed: boolean;
  onMore: () => void;
}) => {
  const end = useRef<HTMLDivElement>(null);
  const canObserve = typeof IntersectionObserver !== "undefined";
  const onMoreRef = useRef(onMore);
  onMoreRef.current = onMore;
  // A failed page stops the auto-load, so it does not retry in a loop; the button retries it.
  const idle = hasMore && !loading && !failed;

  useEffect(() => {
    const node = end.current;
    if (!idle || !node || !canObserve) return undefined;
    const watcher = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onMoreRef.current();
      },
      { rootMargin: "400px 0px" }
    );
    watcher.observe(node);
    return () => watcher.disconnect();
    // `shown` re-arms the watcher after each page: a short page can leave the end in view.
  }, [idle, canObserve, shown]);

  return (
    <div className="pl-more" ref={end}>
      <span className="pl-meta" aria-live="polite">
        {loading ? "Loading more…" : `${shown} of ${total}`}
      </span>
      {failed ? (
        <button type="button" className="pl-btn is-line" onClick={onMore}>
          Couldn’t load more. Try again
        </button>
      ) : null}
      {idle && !canObserve ? (
        <button type="button" className="pl-btn is-line" onClick={onMore}>
          Load more
        </button>
      ) : null}
    </div>
  );
};

export default ProductListMore;
